/**
 * SuperAdmin — Lean Insights Tab
 * 
 * Tableau de bord des métriques produit selon le framework Lean Analytics
 * adapté à un SaaS RH B2B :
 *  - AARRR (Acquisition / Activation / Retention / Revenue / Referral)
 *  - Engagement RH (funnel d'évaluation, PDF, exports)
 *  - Cohort retention
 *  - Health checks
 *  - Top features
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Skeleton } from './ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from 'recharts';
import {
  Users, Building2, TrendingUp, Activity, DollarSign, Share2,
  Zap, Target, Clock, Download, FileSpreadsheet, AlertTriangle,
  RefreshCw, Sparkles, Rocket,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const RANGE_OPTIONS = [
  { value: '7d', label: '7 derniers jours' },
  { value: '30d', label: '30 derniers jours' },
  { value: '90d', label: '90 derniers jours' },
  { value: 'all', label: 'Depuis le début' },
];

// ─────────────────────────────────────────────────────────────
// KPI Card primitive
// ─────────────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, hint, color = 'indigo', testId }) => (
  <Card className="border-slate-200" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-${color}-100`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ─────────────────────────────────────────────────────────────
// Funnel display (vertical bars with rate)
// ─────────────────────────────────────────────────────────────
const FunnelSteps = ({ steps, color = 'indigo' }) => (
  <div className="space-y-3">
    {steps.map((s, idx) => (
      <div key={idx} data-testid={`funnel-step-${idx}`}>
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="font-medium text-slate-700">{s.step}</span>
          <span className="text-slate-500">
            <strong className="text-slate-900">{s.count}</strong> · {s.rate}%
          </span>
        </div>
        <Progress
          value={s.rate}
          className="h-2"
          indicatorClassName={`bg-${color}-500`}
        />
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function SuperAdminLeanInsights() {
  const [range, setRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [aarrr, setAarrr] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [health, setHealth] = useState(null);
  const [topFeatures, setTopFeatures] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [aRes, eRes, cRes, hRes, tRes] = await Promise.all([
        api.get(`/admin/analytics/aarrr?range=${range}`),
        api.get(`/admin/analytics/engagement?range=${range}`),
        api.get('/admin/analytics/cohorts?weeks=8'),
        api.get('/admin/analytics/health'),
        api.get(`/admin/analytics/top-features?range=${range}&limit=15`),
      ]);
      setAarrr(aRes.data);
      setEngagement(eRes.data);
      setCohorts(cRes.data);
      setHealth(hRes.data);
      setTopFeatures(tRes.data);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors du chargement des insights");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [range]);

  useEffect(() => {
    setLoading(true);
    fetchAll();
  }, [fetchAll]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAll();
  };

  // ─── Cohort heatmap color ───
  const cohortColor = (pct) => {
    if (pct === 0) return 'bg-slate-100 text-slate-400';
    if (pct < 25) return 'bg-red-100 text-red-700';
    if (pct < 50) return 'bg-amber-100 text-amber-700';
    if (pct < 75) return 'bg-blue-100 text-blue-700';
    return 'bg-emerald-100 text-emerald-700';
  };

  const acquisitionSeries = useMemo(() => {
    if (!aarrr) return [];
    // Merge companies + users series by date
    const map = {};
    (aarrr.acquisition.companiesSeries || []).forEach(p => {
      map[p.date] = { date: p.date, companies: p.count, users: 0 };
    });
    (aarrr.acquisition.usersSeries || []).forEach(p => {
      if (!map[p.date]) map[p.date] = { date: p.date, companies: 0, users: 0 };
      map[p.date].users = p.count;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [aarrr]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lean-insights-section">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            Insights Lean Startup
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Build · Measure · Learn — Métriques AARRR adaptées à un SaaS RH
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[200px]" data-testid="range-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value} data-testid={`range-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      <Tabs defaultValue="aarrr" className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="aarrr" data-testid="tab-aarrr">
            <Rocket className="w-4 h-4 mr-2" />
            AARRR
          </TabsTrigger>
          <TabsTrigger value="engagement" data-testid="tab-engagement">
            <Activity className="w-4 h-4 mr-2" />
            Engagement
          </TabsTrigger>
          <TabsTrigger value="cohorts" data-testid="tab-cohorts">
            <Users className="w-4 h-4 mr-2" />
            Cohortes
          </TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Santé
          </TabsTrigger>
          <TabsTrigger value="features" data-testid="tab-features">
            <Zap className="w-4 h-4 mr-2" />
            Top features
          </TabsTrigger>
        </TabsList>

        {/* ═══════════ TAB 1 : AARRR ═══════════ */}
        <TabsContent value="aarrr" className="mt-6 space-y-6">
          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              icon={Building2}
              label="Nouvelles entreprises"
              value={aarrr?.acquisition?.newCompanies ?? 0}
              hint={`Sur la période`}
              color="indigo"
              testId="kpi-new-companies"
            />
            <KpiCard
              icon={Users}
              label="Nouveaux utilisateurs"
              value={aarrr?.acquisition?.newUsers ?? 0}
              hint={`Sur la période`}
              color="blue"
              testId="kpi-new-users"
            />
            <KpiCard
              icon={Activity}
              label="DAU / MAU"
              value={`${aarrr?.retention?.dau ?? 0} / ${aarrr?.retention?.mau ?? 0}`}
              hint={`Stickiness · ${aarrr?.retention?.stickiness ?? 0}%`}
              color="emerald"
              testId="kpi-dau-mau"
            />
            <KpiCard
              icon={DollarSign}
              label="Conversion Free → Pro"
              value={`${aarrr?.revenue?.conversionRate ?? 0}%`}
              hint={`${aarrr?.revenue?.pro ?? 0} Pro / ${aarrr?.revenue?.free ?? 0} Free`}
              color="green"
              testId="kpi-conversion"
            />
          </div>

          {/* Acquisition Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Acquisition au fil du temps</CardTitle>
              <CardDescription>Inscriptions d'entreprises et d'utilisateurs par jour</CardDescription>
            </CardHeader>
            <CardContent>
              {acquisitionSeries.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Aucune donnée pour la période sélectionnée</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={acquisitionSeries}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="companies" stroke="#6366f1" fill="url(#g1)" name="Entreprises" />
                    <Area type="monotone" dataKey="users" stroke="#3b82f6" fill="url(#g2)" name="Utilisateurs" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Activation Funnel + Revenue */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-indigo-600" />
                  Funnel d'activation
                </CardTitle>
                <CardDescription>% des entreprises ayant atteint chaque milestone clé</CardDescription>
              </CardHeader>
              <CardContent>
                <FunnelSteps steps={aarrr?.activation?.funnel ?? []} color="indigo" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  Revenue & abonnements
                </CardTitle>
                <CardDescription>Distribution des plans</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-500">Plan gratuit</p>
                    <p className="text-2xl font-bold text-slate-900">{aarrr?.revenue?.free ?? 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                    <p className="text-xs text-emerald-700">Plan Pro</p>
                    <p className="text-2xl font-bold text-emerald-900">{aarrr?.revenue?.pro ?? 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
                    <p className="text-xs text-purple-700">Essai</p>
                    <p className="text-2xl font-bold text-purple-900">{aarrr?.revenue?.trial ?? 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-xs text-red-700">Suspendu</p>
                    <p className="text-2xl font-bold text-red-900">{aarrr?.revenue?.suspended ?? 0}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Taux de conversion</span>
                    <span className="font-bold text-emerald-600">{aarrr?.revenue?.conversionRate ?? 0}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Referral */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Share2 className="w-5 h-5 text-pink-600" />
                Referral & viralité
              </CardTitle>
              <CardDescription>Liens de partage du trombinoscope (vecteur de viralité)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <KpiCard icon={Share2} label="Liens créés" value={aarrr?.referral?.shareLinksTotal ?? 0} color="pink" />
                <KpiCard icon={Activity} label="Liens actifs" value={aarrr?.referral?.shareLinksActive ?? 0} color="emerald" />
                <KpiCard icon={TrendingUp} label="Vues cumulées" value={aarrr?.referral?.shareViews ?? 0} color="indigo" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══════════ TAB 2 : Engagement ═══════════ */}
        <TabsContent value="engagement" className="mt-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              icon={Target}
              label="Évaluations totales"
              value={engagement?.totalEvaluations ?? 0}
              hint="Tous statuts confondus"
              color="indigo"
            />
            <KpiCard
              icon={Clock}
              label="Cycle moyen"
              value={`${engagement?.avgCycleDays ?? 0}j`}
              hint="Self-eval → validation RH"
              color="amber"
            />
            <KpiCard
              icon={Download}
              label="PDF téléchargés"
              value={engagement?.pdfDownloads ?? 0}
              hint="Sur la période"
              color="red"
            />
            <KpiCard
              icon={Sparkles}
              label="Crédits IA consommés"
              value={engagement?.aiCreditsUsed ?? 0}
              hint="Sur la période"
              color="purple"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Funnel d'évaluation</CardTitle>
              <CardDescription>Taux d'achèvement par étape</CardDescription>
            </CardHeader>
            <CardContent>
              <FunnelSteps steps={engagement?.funnel ?? []} color="emerald" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Auto-évaluations soumises</CardTitle>
              <CardDescription>Évolution sur la période</CardDescription>
            </CardHeader>
            <CardContent>
              {(engagement?.evalsSeries ?? []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Aucune auto-évaluation soumise sur la période</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={engagement.evalsSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10b981" name="Soumissions" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══════════ TAB 3 : Cohortes ═══════════ */}
        <TabsContent value="cohorts" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Cohort retention (8 semaines)</CardTitle>
              <CardDescription>
                Chaque ligne = semaine d'inscription. Les cellules indiquent le % d'utilisateurs encore actifs N semaines après.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(cohorts?.cohorts ?? []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Pas assez de données pour calculer les cohortes</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="cohort-table">
                    <thead>
                      <tr className="text-slate-500 border-b">
                        <th className="text-left py-2 px-2 font-medium">Cohorte</th>
                        <th className="text-center py-2 px-2 font-medium">Taille</th>
                        {Array.from({ length: cohorts.weeks }, (_, i) => (
                          <th key={i} className="text-center py-2 px-2 font-medium">S{i}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cohorts.cohorts.map(c => (
                        <tr key={c.cohort} className="border-b">
                          <td className="py-2 px-2 font-medium text-slate-700">{c.cohort}</td>
                          <td className="text-center py-2 px-2 text-slate-500">{c.size}</td>
                          {c.values.map(v => (
                            <td key={v.week} className="text-center py-1 px-1">
                              <span
                                className={`inline-block w-12 px-2 py-1 rounded text-xs font-medium ${cohortColor(v.pct)}`}
                                title={`${v.active} actif(s) sur ${c.size}`}
                              >
                                {v.pct > 0 ? `${v.pct}%` : '—'}
                              </span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══════════ TAB 4 : Health ═══════════ */}
        <TabsContent value="health" className="mt-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              icon={Building2}
              label="Entreprises inactives"
              value={(health?.inactiveCompanies ?? []).length}
              hint=">14 jours sans connexion"
              color="amber"
            />
            <KpiCard
              icon={Users}
              label="Comptes orphelins"
              value={health?.orphanUsers ?? 0}
              hint="Company manquante"
              color="red"
            />
            <KpiCard
              icon={Target}
              label="Campagnes bloquées"
              value={(health?.stuckCampaigns ?? []).length}
              hint=">90j sans validation"
              color="orange"
            />
            <KpiCard
              icon={Sparkles}
              label="Crédits IA bas"
              value={(health?.lowCredits ?? []).length}
              hint="Solde < 10"
              color="purple"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-lg">Entreprises inactives</CardTitle></CardHeader>
              <CardContent>
                {(health?.inactiveCompanies ?? []).length === 0 ? (
                  <p className="text-sm text-emerald-600">✓ Toutes les entreprises sont actives</p>
                ) : (
                  <ul className="space-y-1 max-h-64 overflow-y-auto">
                    {health.inactiveCompanies.map(c => (
                      <li key={c.id} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                        <span className="text-slate-700">{c.name}</span>
                        <Badge variant="outline" className="text-xs">Inactive</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-lg">Crédits IA en alerte</CardTitle></CardHeader>
              <CardContent>
                {(health?.lowCredits ?? []).length === 0 ? (
                  <p className="text-sm text-emerald-600">✓ Aucune entreprise sous le seuil</p>
                ) : (
                  <ul className="space-y-1 max-h-64 overflow-y-auto">
                    {health.lowCredits.map(c => (
                      <li key={c.companyId} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                        <span className="text-slate-700">{c.companyName}</span>
                        <Badge className="text-xs bg-purple-100 text-purple-700">{c.balance} crédits</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ═══════════ TAB 5 : Top features ═══════════ */}
        <TabsContent value="features" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Top fonctionnalités utilisées
              </CardTitle>
              <CardDescription>
                Total : <strong>{topFeatures?.totalEvents ?? 0}</strong> événements sur la période
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(topFeatures?.features ?? []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">
                  Aucun événement sur la période — le tracking commencera à enregistrer dès la prochaine action utilisateur.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(240, (topFeatures.features.length || 1) * 32)}>
                  <BarChart data={topFeatures.features} layout="vertical" margin={{ left: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="feature" tick={{ fontSize: 11 }} width={150} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
